/**
 * Disposable Email Detection
 *
 * Blocks temporary/disposable email addresses to prevent spam registrations.
 * This list includes popular disposable email services.
 *
 * Updated: 2025-12
 */

// Common disposable email domains (comprehensive list)
export const DISPOSABLE_EMAIL_DOMAINS: Set<string> = new Set([
  // Popular disposable email services
  'maildrop.cc',
  'mailinator.com',
  'guerrillamail.com',
  'guerrillamail.info',
  'guerrillamail.net',
  'guerrillamail.org',
  'guerrillamail.biz',
  'guerrillamail.de',
  'sharklasers.com',
  'grr.la',
  'guerrillamailblock.com',
  'pokemail.net',
  'spam4.me',
  '10minutemail.com',
  '10minutemail.net',
  '10minutemail.org',
  '10minutemail.co.uk',
  '10minutemail.de',
  'tempmail.com',
  'tempmail.net',
  'temp-mail.org',
  'temp-mail.io',
  'temp-mail.ru',
  'tempail.com',
  'tempr.email',
  'throwaway.email',
  'throwawaymail.com',
  'fakeinbox.com',
  'fakemailgenerator.com',
  'getnada.com',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'cool.fr.nf',
  'jetable.fr.nf',
  'nospam.ze.tc',
  'nomail.xl.cx',
  'mega.zik.dj',
  'speed.1s.fr',
  'courriel.fr.nf',
  'moncourrier.fr.nf',
  'monemail.fr.nf',
  'monmail.fr.nf',
  'discard.email',
  'discardmail.com',
  'discardmail.de',
  'spamgourmet.com',
  'trashmail.com',
  'trashmail.de',
  'trashmail.me',
  'trashmail.net',
  'trashmail.org',
  'trashmail.ws',
  'mailnesia.com',
  'mailnator.com',
  'spamex.com',
  'getairmail.com',
  'mailcatch.com',
  'tempmailaddress.com',
  'tmpmail.org',
  'tmpmail.net',
  'mohmal.com',
  'mohmal.im',
  'mohmal.in',
  'mohmal.tech',
  'emailondeck.com',
  'mailsac.com',
  'mytrashmail.com',
  'emailfake.com',
  'crazymailing.com',
  'tempsky.com',
  'tempmails.net',
  'anonymbox.com',
  'burnermail.io',
  'spambox.us',
  'spamfree24.org',
  'spamfree24.de',
  'spamfree24.eu',
  'spamfree24.info',
  'spamfree24.net',
  'spamfree.eu',
  'wegwerfmail.de',
  'wegwerfmail.net',
  'wegwerfmail.org',
  'einrot.de',
  'einrot.com',
  'imgof.com',
  'imgof.net',
  'sofort-mail.de',
  'sofortmail.de',
  'tradermail.info',
  'rcpt.at',
  'trash-mail.at',
  'trash-mail.com',
  'trash-mail.de',
  'proxymail.eu',
  'mail-temporaire.fr',
  'mintemail.com',
  'inboxalias.com',
  'mailforspam.com',
  'objectmail.com',
  'proxymail.eu',
  'rcpt.at',
  'trash-mail.at',
  'kurzepost.de',
  'armyspy.com',
  'cuvox.de',
  'dayrep.com',
  'einrot.com',
  'fleckens.hu',
  'gustr.com',
  'jourrapide.com',
  'rhyta.com',
  'superrito.com',
  'teleworm.us',
  'pokemail.net',
  'mailpoof.com',
  'emailsensei.com',
  'spamobox.com',
  'thankyou2010.com',
  'tempomail.fr',
  'spamspot.com',
  'mailslite.com',
  'mailzilla.org',
  'mailzilla.com',
  'alivance.com',
  'deadaddress.com',
  'deadfake.cf',
  'deadfake.ga',
  'deadfake.ml',
  'deadfake.tk',
  'despam.it',
  'despammed.com',
  'devnullmail.com',
  'dfgh.net',
  'digitalsanctuary.com',
  'disposableaddress.com',
  'disposableemailaddresses.com',
  'disposableinbox.com',
  'dispose.it',
  'dispostable.com',
  'dm.w3internet.co.uk',
  'dodgeit.com',
  'dodgemail.de',
  'dodgit.com',
  'dodgit.org',
  'dontsendmespam.de',
  'dump-email.info',
  'dumpandjunk.com',
  'dumpmail.de',
  'dumpyemail.com',
  'e4ward.com',
  'easytrashmail.com',
  'email60.com',
  'emailgo.de',
  'emailias.com',
  'emailigo.de',
  'emailinfive.com',
  'emaillime.com',
  'emailmiser.com',
  'emails.ga',
  'emailsphere.com',
  'emailtemporanea.com',
  'emailtemporanea.net',
  'emailtemporar.ro',
  'emailtemporario.com.br',
  'emailthe.net',
  'emailtmp.com',
  'emailto.de',
  'emailwarden.com',
  'emailx.at.hm',
  'emailxfer.com',
  'emz.net',
  'enterto.com',
  'ephemail.net',
  'etranquil.com',
  'etranquil.net',
  'etranquil.org',
  'evopo.com',
  'explodemail.com',
  'express.net.ua',
  'eyepaste.com',
  'fakeinbox.cf',
  'fakeinbox.com',
  'fakeinbox.ga',
  'fakeinbox.ml',
  'fakeinbox.tk',
  'fakemailz.com',
  'fammix.com',
  'fastacura.com',
  'fastchevy.com',
  'fastchrysler.com',
  'fastkawasaki.com',
  'fastmazda.com',
  'fastmitsubishi.com',
  'fastnissan.com',
  'fastsubaru.com',
  'fastsuzuki.com',
  'fasttoyota.com',
  'fastyamaha.com',
  'fettomeansen.com',
  'filzmail.com',
  'fizmail.com',
  'flyspam.com',
  'fr33mail.info',
  'frapmail.com',
  'friendlymail.co.uk',
  'front14.org',
  'fuckingduh.com',
  'fudgerub.com',
  'garliclife.com',
  'gehensiull.com',
  'get1mail.com',
  'get2mail.fr',
  'getonemail.com',
  'getonemail.net',
  'ghosttexter.de',
  'giantmail.de',
  'girlsundertheinfluence.com',
  'gishpuppy.com',
  'gmial.com',
  'goemailgo.com',
  'gorillaswithdirtyarmpits.com',
  'gotmail.com',
  'gotmail.net',
  'gotmail.org',
  'gotti.otherinbox.com',
  'gowikibooks.com',
  'gowikicampus.com',
  'gowikicars.com',
  'gowikifilms.com',
  'gowikigames.com',
  'gowikimusic.com',
  'gowikinetwork.com',
  'gowikitravel.com',
  'gowikitv.com',
  'great-host.in',
  'greensloth.com',
  'h.mintemail.com',
  'haltospam.com',
  'hatespam.org',
  'herp.in',
  'hidemail.de',
  'hidzz.com',
  'hochsitze.com',
  'hopemail.biz',
  'hotpop.com',
  'hulapla.de',
  'hush.ai',
  'hush.com',
  'hushmail.com',
  'hushmail.me',
  'ieatspam.eu',
  'ieatspam.info',
  'ieh-mail.de',
  'ihateyoualot.info',
  'imails.info',
  'imgof.com',
  'imgv.de',
  'imstations.com',
  'inbax.tk',
  'inbox.si',
  'inboxalias.com',
  'inboxclean.com',
  'inboxclean.org',
  'inboxed.im',
  'inboxed.pw',
  'incognitomail.com',
  'incognitomail.net',
  'incognitomail.org',
  'insorg-mail.info',
  'instant-mail.de',
  'instantemailaddress.com',
  'ipoo.org',
  'irish2me.com',
  'iwi.net',
  'jetable.com',
  'jetable.fr.nf',
  'jetable.net',
  'jetable.org',
  'jnxjn.com',
  'jobbikszansen.com',
  'jourrapide.com',
  'jsrsolutions.com',
  'kasmail.com',
  'kaspop.com',
  'keepmymail.com',
  'killmail.com',
  'killmail.net',
  'kimsdisk.com',
  'kingsq.ga',
  'kiois.com',
  'klassmaster.com',
  'klassmaster.net',
  'klzlv.com',
  'kook.ml',
  'kulturbetrieb.info',
  'kurzepost.de',
  'l33r.eu',
  'lackmail.net',
  'lags.us',
  'landmail.co',
  'laoeq.com',
  'lastmail.co',
  'lawlita.com',
  'lazyinbox.com',
  'letthemeatspam.com',
  'lhsdv.com',
  'lifetoithout.com',
  'lifetipps.com',
  'link2mail.net',
  'litedrop.com',
  'liveradio.tk',
  'loadby.us',
  'login-email.cf',
  'login-email.ga',
  'login-email.ml',
  'login-email.tk',
  'lol.ovpn.to',
  'lookugly.com',
  'lopl.co.cc',
  'lortemail.dk',
  'lovemeleaveme.com',
  'lr78.com',
  'lroid.com',
  'lukop.dk',
  'm21.cc',
  'm4ilweb.info',
  'maboard.com',
  'mail-hierarchie.net',
  'mail-temporaire.fr',
  'mail.by',
  'mail.mezimages.net',
  'mail.zp.ua',
  'mail114.net',
  'mail2rss.org',
  'mail333.com',
  'mail4trash.com',
  'mailbidon.com',
  'mailblocks.com',
  'mailcatch.com',
  'mailde.de',
  'mailde.info',
  'maildrop.cc',
  'maildrop.cf',
  'maildrop.ga',
  'maildrop.gq',
  'maildrop.ml',
  'maildu.de',
  'maildx.com',
  'mailed.ro',
  'maileater.com',
  'mailexpire.com',
  'mailfa.tk',
  'mailfreeonline.com',
  'mailguard.me',
  'mailhazard.com',
  'mailhazard.us',
  'mailhz.me',
  'mailimate.com',
  'mailin8r.com',
  'mailinater.com',
  'mailinator.co.uk',
  'mailinator.info',
  'mailinator.net',
  'mailinator.org',
  'mailinator.us',
  'mailinator2.com',
  'mailincubator.com',
  'mailismagic.com',
  'mailjunk.cf',
  'mailjunk.ga',
  'mailjunk.gq',
  'mailjunk.ml',
  'mailjunk.tk',
  'mailme.gq',
  'mailme.ir',
  'mailme.lv',
  'mailme24.com',
  'mailmetrash.com',
  'mailmoat.com',
  'mailnator.com',
  'mailnesia.com',
  'mailnull.com',
  'mailorg.org',
  'mailpick.biz',
  'mailrock.biz',
  'mailsac.com',
  'mailseal.de',
  'mailshell.com',
  'mailsiphon.com',
  'mailslapping.com',
  'mailslite.com',
  'mailtothis.com',
  'mailzilla.com',
  'mailzilla.org',
  'mailzilla.orgmbx.cc',
  'makemetheking.com',
  'manifestgenerator.com',
  'manybrain.com',
  'mbx.cc',
  'mega.zik.dj',
  'meinspamschutz.de',
  'meltmail.com',
  'messagebeamer.de',
  'mierdamail.com',
  'migmail.pl',
  'migumail.com',
  'mintemail.com',
  'mjukgansen.nu',
  'moakt.com',
  'mobi.web.id',
  'mobileninja.co.uk',
  'moburl.com',
  'moncourrier.fr.nf',
  'monemail.fr.nf',
  'monmail.fr.nf',
  'monumentmail.com',
  'ms9.mailslite.com',
  'msa.minsmail.com',
  'msb.minsmail.com',
  'msg.mailslite.com',
  'mspeciosa.com',
  'mswork.ru',
  'mtmdev.com',
  'mucincanon.com',
  'mvrht.com',
  'mwarner.org',
  'mx0.wwwnew.eu',
  'my10minutemail.com',
  'mycleaninbox.net',
  'myemailboxy.com',
  'mymail-in.net',
  'mymailoasis.com',
  'mynetstore.de',
  'mypacks.net',
  'mypartyclip.de',
  'myphantomemail.com',
  'mysamp.de',
  'myspaceinc.com',
  'myspaceinc.net',
  'myspacepimpedup.com',
  'mytempemail.com',
  'mytempmail.com',
  'mytrashmail.com',
  'nabuma.com',
  'neomailbox.com',
  'nepwk.com',
  'nervmich.net',
  'nervtmansen.com',
  'netmails.com',
  'netmails.net',
  'netzidiot.de',
  'neverbox.com',
  'nice-4u.com',
  'nigge.rs',
  'nincsmail.hu',
  'nmail.cf',
  'no-spam.ws',
  'nobulk.com',
  'noclickemail.com',
  'nogmailspam.info',
  'nomail.xl.cx',
  'nomail2me.com',
  'nomorespamemails.com',
  'nonspam.eu',
  'nonspammer.de',
  'noref.in',
  'nospam.ze.tc',
  'nospam4.us',
  'nospamfor.us',
  'nospammail.net',
  'nospamthanks.info',
  'notmailinator.com',
  'nowhere.org',
  'nowmymail.com',
  'nurfuerspam.de',
  'nus.edu.sg',
  'nwldx.com',
  'objectmail.com',
  'obobbo.com',
  'odnorazovoe.ru',
  'one-time.email',
  'oneoffemail.com',
  'onewaymail.com',
  'onlatedotcom.info',
  'online.ms',
  'oopi.org',
  'opayq.com',
  'ordinaryamerican.net',
  'otherinbox.com',
  'ourklips.com',
  'outlawspam.com',
  'ovpn.to',
  'owlpic.com',
  'pancakemail.com',
  'pjjkp.com',
  'plexolan.de',
  'poczta.onet.pl',
  'politikerclub.de',
  'poofy.org',
  'pookmail.com',
  'powered.name',
  'privacy.net',
  'privy-mail.com',
  'privymail.de',
  'proxymail.eu',
  'prtnx.com',
  'punkass.com',
  'putthisinyourspamdatabase.com',
  'qq.com',
  'quickinbox.com',
  'quickmail.nl',
  'rainmail.biz',
  'rcpt.at',
  're-gister.com',
  'reallymymail.com',
  'realtyalerts.ca',
  'recode.me',
  'reconmail.com',
  'recursor.net',
  'regbypass.com',
  'regbypass.comsafe-mail.net',
  'rejectmail.com',
  'reliable-mail.com',
  'remail.cf',
  'remail.ga',
  'remote.li',
  'rhyta.com',
  'rklips.com',
  'rmqkr.net',
  'royal.net',
  'rppkn.com',
  'rtrtr.com',
  's0ny.net',
  'safe-mail.net',
  'safersignup.de',
  'safetymail.info',
  'safetypost.de',
  'sandelf.de',
  'saynotospams.com',
  'schafmail.de',
  'schrott-email.de',
  'secretemail.de',
  'secure-mail.biz',
  'selfdestructingmail.com',
  'senseless-entertainment.com',
  'server.ms.selfip.net',
  'sharklasers.com',
  'shieldemail.com',
  'shiftmail.com',
  'shitmail.me',
  'shitmail.org',
  'shitware.nl',
  'shortmail.net',
  'shut.name',
  'shut.ws',
  'sibmail.com',
  'sify.com',
  'sinnlos-mail.de',
  'siteposter.net',
  'skeefmail.com',
  'slaskpost.se',
  'slopsbox.com',
  'slowslow.de',
  'smapfree24.com',
  'smapfree24.de',
  'smapfree24.eu',
  'smapfree24.info',
  'smapfree24.org',
  'smashmail.de',
  'smellfear.com',
  'snakemail.com',
  'sneakemail.com',
  'sneakmail.de',
  'snkmail.com',
  'sofimail.com',
  'sofort-mail.de',
  'sogetthis.com',
  'sohu.com',
  'soisz.com',
  'solvemail.info',
  'soodonims.com',
  'spam.la',
  'spam.su',
  'spam4.me',
  'spamail.de',
  'spamarrest.com',
  'spamavert.com',
  'spambob.com',
  'spambob.net',
  'spambob.org',
  'spambog.com',
  'spambog.de',
  'spambog.net',
  'spambog.ru',
  'spambox.info',
  'spambox.irishspringrealty.com',
  'spambox.us',
  'spamcannon.com',
  'spamcannon.net',
  'spamcero.com',
  'spamcon.org',
  'spamcorptastic.com',
  'spamcowboy.com',
  'spamcowboy.net',
  'spamcowboy.org',
  'spamday.com',
  'spameater.com',
  'spameater.org',
  'spamex.com',
  'spamfighter.cf',
  'spamfighter.ga',
  'spamfighter.gq',
  'spamfighter.ml',
  'spamfighter.tk',
  'spamfree.eu',
  'spamfree24.com',
  'spamfree24.de',
  'spamfree24.eu',
  'spamfree24.info',
  'spamfree24.net',
  'spamfree24.org',
  'spamgoes.in',
  'spamgourmet.com',
  'spamgourmet.net',
  'spamgourmet.org',
  'spamherelots.com',
  'spamhereplease.com',
  'spamhole.com',
  'spamify.com',
  'spaminator.de',
  'spamkill.info',
  'spaml.com',
  'spaml.de',
  'spamlot.net',
  'spammotel.com',
  'spamobox.com',
  'spamoff.de',
  'spamslicer.com',
  'spamspot.com',
  'spamstack.net',
  'spamthis.co.uk',
  'spamthisplease.com',
  'spamtroll.net',
  'speed.1s.fr',
  'spoofmail.de',
  'squizzy.de',
  'ssoia.com',
  'startkeys.com',
  'stinkefinger.net',
  'stop-my-spam.cf',
  'stop-my-spam.com',
  'stop-my-spam.ga',
  'stop-my-spam.ml',
  'stop-my-spam.tk',
  'streetwisemail.com',
  'stuffmail.de',
  'super-auswahl.de',
  'supergreatmail.com',
  'supermailer.jp',
  'superrito.com',
  'superstachel.de',
  'suremail.info',
  'svk.jp',
  'sweetxxx.de',
  'tafmail.com',
  'tagyourself.com',
  'talkinator.com',
  'tapchicuoihoi.com',
  'techemail.com',
  'techgroup.me',
  'teewars.org',
  'teleworm.com',
  'teleworm.us',
  'temp-mail.de',
  'temp-mail.org',
  'temp-mail.ru',
  'temp.emeraldwebmail.com',
  'temp.headstrong.de',
  'tempalias.com',
  'tempail.com',
  'tempe-mail.com',
  'tempemail.biz',
  'tempemail.co.za',
  'tempemail.com',
  'tempemail.net',
  'tempemail.org',
  'tempinbox.co.uk',
  'tempinbox.com',
  'tempmail.co',
  'tempmail.de',
  'tempmail.eu',
  'tempmail.it',
  'tempmail.net',
  'tempmail.us',
  'tempmail2.com',
  'tempmaildemo.com',
  'tempmailer.com',
  'tempmailer.de',
  'tempomail.fr',
  'temporarily.de',
  'temporarioemail.com.br',
  'temporaryemail.net',
  'temporaryemail.us',
  'temporaryforwarding.com',
  'temporaryinbox.com',
  'temporarymailaddress.com',
  'tempthe.net',
  'temptmail.com',
  'thankyou2010.com',
  'thecloudindex.com',
  'thisisnotmyrealemail.com',
  'throam.com',
  'throwam.com',
  'throwawayemailaddress.com',
  'throwawaymail.com',
  'tilien.com',
  'tittbit.in',
  'tmailinator.com',
  'toiea.com',
  'toomail.biz',
  'topfreeemail.com',
  'topranklist.de',
  'tradermail.info',
  'trash-amil.com',
  'trash-mail.at',
  'trash-mail.com',
  'trash-mail.de',
  'trash-mail.gq',
  'trash2009.com',
  'trash2010.com',
  'trash2011.com',
  'trashbox.eu',
  'trashdevil.com',
  'trashdevil.de',
  'trashemail.de',
  'trashmail.at',
  'trashmail.com',
  'trashmail.de',
  'trashmail.me',
  'trashmail.net',
  'trashmail.org',
  'trashmail.ws',
  'trashmailer.com',
  'trashymail.com',
  'trashymail.net',
  'trbvm.com',
  'trickmail.net',
  'trillianpro.com',
  'tryalert.com',
  'turual.com',
  'twinmail.de',
  'twoweirdtricks.com',
  'tyldd.com',
  'ubismail.net',
  'uggsrock.com',
  'umail.net',
  'upliftnow.com',
  'uplipht.com',
  'uroid.com',
  'us.af',
  'valemail.net',
  'venompen.com',
  'veryrealemail.com',
  'viditag.com',
  'viewcastmedia.com',
  'viewcastmedia.net',
  'viewcastmedia.org',
  'viralplays.com',
  'vkcode.ru',
  'vpn.st',
  'vsimcard.com',
  'vubby.com',
  'walala.org',
  'walkmail.net',
  'walkmail.ru',
  'webemail.me',
  'webm4il.info',
  'webuser.in',
  'wee.my',
  'weg-werf-email.de',
  'wegwerf-email-addressen.de',
  'wegwerf-emails.de',
  'wegwerfadresse.de',
  'wegwerfemail.com',
  'wegwerfemail.de',
  'wegwerfmail.de',
  'wegwerfmail.info',
  'wegwerfmail.net',
  'wegwerfmail.org',
  'wetrainbayarea.com',
  'wetrainbayarea.org',
  'wh4f.org',
  'whatiaas.com',
  'whatpaas.com',
  'whopy.com',
  'whtjddn.33mail.com',
  'whyspam.me',
  'wilemail.com',
  'willhackforfood.biz',
  'willselfdestruct.com',
  'winemaven.info',
  'wolfsmail.tk',
  'wollan.info',
  'worldspace.link',
  'wronghead.com',
  'wuzup.net',
  'wuzupmail.net',
  'wwwnew.eu',
  'x.ip6.li',
  'xagloo.co',
  'xagloo.com',
  'xemaps.com',
  'xents.com',
  'xmaily.com',
  'xoxy.net',
  'yapped.net',
  'yep.it',
  'yogamaven.com',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.gq',
  'yopmail.net',
  'you-spam.com',
  'ypmail.webarnak.fr.eu.org',
  'yuurok.com',
  'z1p.biz',
  'za.com',
  'zehnminuten.de',
  'zehnminutenmail.de',
  'zetmail.com',
  'zippymail.info',
  'zoaxe.com',
  'zoemail.com',
  'zoemail.net',
  'zoemail.org',
  'zomg.info',
  'zxcv.com',
  'zxcvbnm.com',
  'zzz.com',
])

/**
 * Check if an email is from a disposable email provider
 * @param email - Email address to check
 * @returns true if the email is disposable, false otherwise
 */
export function isDisposableEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false
  }

  const domain = email.toLowerCase().split('@')[1]
  if (!domain) {
    return false
  }

  return DISPOSABLE_EMAIL_DOMAINS.has(domain)
}

/**
 * Get the domain from an email address
 * @param email - Email address
 * @returns Domain portion of the email or null if invalid
 */
export function getEmailDomain(email: string): string | null {
  if (!email || typeof email !== 'string') {
    return null
  }

  const parts = email.toLowerCase().split('@')
  return parts.length === 2 ? parts[1] : null
}
